import {
  CognitoUserAttribute,
  CognitoUserPool,
} from "amazon-cognito-identity-js";
import { Link, createFileRoute } from "@tanstack/react-router";
import { FormEvent, useState } from "react";

import { SelectedCellRegion } from "@/components/auth/selected-cell-region";
import { Button } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cellConfig } from "@/lib/cell";
import useDeflowSessionStore, {
  DeflowSessionStates,
} from "@/stores/useDeflowSessionStore";

export const Route = createFileRoute("/plugin/figma/sign-up")({
  component: SignUp,
});

interface CustomElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface CustomForm extends HTMLFormElement {
  readonly elements: CustomElements;
}

function SignUp() {
  const { sessionState } = useDeflowSessionStore();

  const [isLoading] = useState<boolean>(false);

  const onSubmit = (event: FormEvent<CustomForm>) => {
    event.preventDefault();

    const email = event.currentTarget.elements.email.value;
    const password = event.currentTarget.elements.password.value;

    const cell = cellConfig();
    const userPool = new CognitoUserPool({
      UserPoolId: cell.userPoolId,
      ClientId: cell.userPoolClientId,
    });

    const attributes = [
      new CognitoUserAttribute({ Name: "email", Value: email }),
    ];

    userPool.signUp(email, password, attributes, [], () => {});
  };

  return (
    <div className="p-8">
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Sign up</h1>
          <p className="text-sm text-muted-foreground">
            Enter your email below to create your account.
          </p>
        </div>
        <SelectedCellRegion
          nextPath="/plugin/figma/sign-up"
          regionPickerPath="/plugin/figma/region"
        />
        <div className={"grid gap-6"}>
          <form onSubmit={onSubmit}>
            <div className="grid gap-2">
              <div className="grid gap-1">
                <Label className="sr-only" htmlFor="email">
                  Email
                </Label>
                <Input
                  id="email"
                  placeholder="Email"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                />
                <Label className="sr-only" htmlFor="password">
                  Password
                </Label>
                <Input
                  id="password"
                  placeholder="Password"
                  type="password"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                />
                <Label className="sr-only" htmlFor="password">
                  Confirm password
                </Label>
                <Input
                  id="password"
                  placeholder="Confirm password"
                  type="password"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  disabled={isLoading}
                />
              </div>
              {sessionState === DeflowSessionStates.AuthenticationError && (
                <p className="text-sm text-muted-foreground">
                  Could not sign in, email or password might be incorrect.
                </p>
              )}
              <Button disabled={isLoading}>
                {isLoading && (
                  <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
                )}
                Sign up
              </Button>
            </div>
          </form>
        </div>
        <p className="px-8 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/plugin/figma/sign-in"
            className="underline underline-offset-4 hover:text-primary"
          >
            Sign in
          </Link>{" "}
        </p>
        <p className="px-8 text-center text-sm text-muted-foreground">
          By clicking sign up, you agree to our Terms of Service and Privacy
          Policy.
        </p>
      </div>
    </div>
  );
}
